import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { memo, useCallback, useMemo, useState } from 'react';
import SearchPerson from './search-person';
import { SearchPersonVM } from 'front-office/view-models/reprint-cid/form';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { PersonResponse, VisibilityType } from 'front-office/pages/reprint-cid';
import { ItemVM } from 'shell/view-models/item';

interface AccordionPersonProps {
  onChange: (key: keyof SearchPersonVM, value: SearchPersonVM[keyof SearchPersonVM]) => void;
  values?: SearchPersonVM;
  castedAnprData: PersonResponse;
  visibility: VisibilityType;
  setVisibility: (value: VisibilityType) => void;
  disabled?: boolean;
}

const AccordionPersonComponent = ({
  onChange,
  values,
  castedAnprData,
  visibility,
  setVisibility,
  disabled,
}: AccordionPersonProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility({
        ...visibility,
        [type]: visibilityMessage,
      } as VisibilityType);
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.problems?.errors && castedAnprData?.problems?.errors?.length) {
      errorToVisualize.title =
        castedAnprData?.problems?.errors &&
        castedAnprData?.problems?.errors?.length &&
        castedAnprData?.problems?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.problems?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedAnprData?.problems?.errors;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-person-content"
        id="panel-person-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Persona</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <SearchMessages
          type={MessageType.Error}
          title={errorMessage.title || ''}
          messages={errorMessage.messages || []}
          visibility={errorMessage.visibility || false}
          onChangeVisibility={onVisibility}
        />
        <SearchMessages
          type={MessageType.Warning}
          title={
            castedAnprData?.problems?.warnings && castedAnprData?.problems?.warnings?.length > 1
              ? `Ci sono ${castedAnprData?.problems?.warnings?.length} warning`
              : `C'è 1 warning`
          }
          messages={castedAnprData?.problems?.warnings || []}
          visibility={
            visibility.warning !== undefined
              ? visibility.warning
              : !!(
                  castedAnprData?.problems &&
                  castedAnprData?.problems?.warnings &&
                  castedAnprData?.problems?.warnings?.length > 0
                )
          }
          onChangeVisibility={onVisibility}
        />
        <SearchPerson values={values} onChange={onChange} disabled={disabled} />
      </AccordionDetails>
    </Accordion>
  );
};

AccordionPersonComponent.displayName = 'Accordion person';

const AccordionPerson = memo(AccordionPersonComponent);

export default AccordionPerson;
